import axios from "axios";
import { baseUrl } from "../config/apiConfig";
import { getDataFromLocalStorage } from "../util/localStorage";

const API_BASE = process.env.REACT_APP_CHATBOT_API_URL || baseUrl || "http://127.0.0.1:8000";

const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  config.headers["ngrok-skip-browser-warning"] = "true";

  const token = getDataFromLocalStorage("token");
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export { api as chatbotHttp };

// ── Clients ──────────────────────────────────────────────
export const listClients = () => api.get("/clients").then((r) => r.data);
export const createClient = (data) => api.post("/clients", data).then((r) => r.data);
export const getClient = (id) => api.get(`/clients/${id}`).then((r) => r.data);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data).then((r) => r.data);
export const listClientUsers = (clientId) =>
  api.get("/users", { params: { client_id: clientId } }).then((r) => r.data);
export const createClientUser = (data) => api.post("/users", data).then((r) => r.data);
export const activateUser = (userId) => api.post(`/users/${userId}/activate`).then((r) => r.data);
export const deactivateUser = (userId) => api.post(`/users/${userId}/deactivate`).then((r) => r.data);
export const deleteUser = (userId) => api.post(`/users/${userId}/delete`).then((r) => r.data);
export const restoreUser = (userId) => api.post(`/users/${userId}/restore`).then((r) => r.data);
export const updateUser = (userId, data) => api.patch(`/users/${userId}`, data).then((r) => r.data);

// ── Businesses ───────────────────────────────────────────
export const listBusinesses = (clientId) =>
  api.get("/bots", clientId == null ? {} : { params: { client_id: clientId } }).then((r) => {
    const response = r.data;
    return [
      response,
      response?.items,
      response?.bots,
      response?.results,
      response?.data,
      response?.data?.items,
      response?.data?.bots,
      response?.data?.results,
    ].find(Array.isArray) || [];
  });
export const createBot = (clientId, data) =>
  api.post("/bots", { ...data, client_id: clientId }).then((r) => r.data);
export const getBot = (id) => api.get(`/bots/${id}`).then((r) => r.data);
export const getBusiness = (id) => api.get(`/businesses/${id}`).then((r) => r.data);
export const updateBusiness = (id, data) => api.put(`/businesses/${id}`, data).then((r) => r.data);
export const getActiveBusinessFlow = (id) =>
  api.get(`/bots/${id}/flows/active`).then((r) => r.data);
export const publishBusinessFlow = (id, flowJson) =>
  api.post(`/bots/${id}/flows/publish`, { flow_json: flowJson }).then((r) => r.data);

// ── WhatsApp Phone Numbers ────────────────────────────────
export const listPhoneNumbers = (businessId) =>
  api.get(`/businesses/${businessId}/whatsapp-phone-numbers`).then((r) => r.data);
export const createPhoneNumber = (businessId, data) =>
  api.post(`/businesses/${businessId}/whatsapp-phone-numbers`, data).then((r) => r.data);

// ── Tools (global) ───────────────────────────────────────
export const listTools = (clientId) =>
  api.get("/tools", clientId == null ? {} : { params: { client_id: clientId } }).then((r) => r.data);
export const createTool = (data, clientId) =>
  api.post("/tools", { ...data, client_id: clientId ?? data.client_id }).then((r) => r.data);
export const getTool = (id) => api.get(`/tools/${id}`).then((r) => r.data);
export const updateTool = (id, data) => api.put(`/tools/${id}`, data).then((r) => r.data);

// ── Business Tools ────────────────────────────────────────
export const listBusinessTools = (businessId, clientId) =>
  api.get(`/bots/${businessId}/tools`, clientId == null ? {} : { params: { client_id: clientId } }).then((r) => r.data);
export const addBusinessTool = (businessId, data) =>
  api.post(`/bots/${businessId}/tools`, data).then((r) => r.data);
export const updateBusinessTool = (businessId, toolId, data) =>
  api.put(`/bots/${businessId}/tools/${toolId}`, data).then((r) => r.data);
export const deleteBusinessTool = (businessId, toolId) =>
  api.delete(`/bots/${businessId}/tools/${toolId}`);
